import React from 'react';
import { CreditCard, CheckCircle2, Clock, DollarSign, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export const TransactionStatsCards = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="transaction-stats">
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <CreditCard size={20} className="text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-gray-500 text-xs">Total Transacciones</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
            <CheckCircle2 size={20} className="text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{stats.completed}</p>
            <p className="text-gray-500 text-xs">Completadas</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
            <Clock size={20} className="text-yellow-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{stats.pending}</p>
            <p className="text-gray-500 text-xs">Pendientes</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <DollarSign size={20} className="text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">${stats.revenue.toFixed(2)}</p>
            <p className="text-gray-500 text-xs">Ingresos Totales</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-DO', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const getPlanBadge = (planId) => {
  const colors = {
    vip: 'bg-amber-500/20 text-amber-400',
    premium: 'bg-purple-500/20 text-purple-400',
    basic: 'bg-blue-500/20 text-blue-400'
  };
  return <Badge className={colors[planId] || colors.basic}>{planId?.toUpperCase()}</Badge>;
};

export const PendingPaymentsSection = ({ zellePending, predPending, onReview, reviewingId }) => (
  <>
    {zellePending.length > 0 && (
      <Card className="bg-gray-900 border-purple-500/30 mb-6" data-testid="zelle-pending-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <DollarSign size={18} className="text-purple-400" />
            </div>
            Pagos Pendientes ({zellePending.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {zellePending.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-800/50 border border-gray-700/50" data-testid={`zelle-pending-${tx.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-white font-semibold">{tx.user_name || tx.user_email}</p>
                    {getPlanBadge(tx.plan_id)}
                  </div>
                  <p className="text-gray-500 text-xs">{tx.user_email}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-white font-bold">${(tx.amount || 0).toFixed(2)}</span>
                    <span className="text-gray-500">{tx.payment_method === 'cash_app' ? 'Cash App' : tx.payment_method === 'paypal' ? 'PayPal' : 'Zelle'} · Titular: <span className="text-gray-300">{tx.zelle_name}</span></span>
                    {tx.zelle_confirmation && (
                      <span className="text-gray-500">Conf: <span className="text-gray-300 font-mono">{tx.zelle_confirmation}</span></span>
                    )}
                    <span className="text-gray-500">{formatDate(tx.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button size="sm" onClick={() => onReview(tx.id, 'approve')} disabled={reviewingId === tx.id} className="bg-green-600 hover:bg-green-500 text-white" data-testid={`zelle-approve-${tx.id}`}>
                    {reviewingId === tx.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                    Aprobar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReview(tx.id, 'reject')} disabled={reviewingId === tx.id} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`zelle-reject-${tx.id}`}>
                    <XCircle size={14} className="mr-1" />Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}
    {predPending.length > 0 && (
      <Card className="bg-gray-900 border-amber-500/30 mb-6" data-testid="pred-zelle-pending-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <DollarSign size={18} className="text-amber-400" />
            </div>
            Pagos Predicción Pendientes ({predPending.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {predPending.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-800/50 border border-gray-700/50" data-testid={`pred-pending-${tx.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-white font-semibold">{tx.user_name || tx.user_email}</p>
                    <Badge className="bg-amber-500/20 text-amber-400">Prediccion $25</Badge>
                  </div>
                  <p className="text-gray-500 text-xs">{tx.user_email}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-white font-bold">${(tx.amount || 0).toFixed(2)}</span>
                    <span className="text-gray-500">{tx.payment_method === 'cash_app' ? 'Cash App' : tx.payment_method === 'paypal' ? 'PayPal' : 'Zelle'} · Titular: <span className="text-gray-300">{tx.zelle_name}</span></span>
                    {tx.zelle_confirmation && (
                      <span className="text-gray-500">Conf: <span className="text-gray-300 font-mono">{tx.zelle_confirmation}</span></span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button size="sm" onClick={() => onReview(tx.id, 'approve', 'prediction')} disabled={reviewingId === tx.id} className="bg-green-600 hover:bg-green-500 text-white">
                    {reviewingId === tx.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                    Aprobar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReview(tx.id, 'reject', 'prediction')} disabled={reviewingId === tx.id} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <XCircle size={14} className="mr-1" />Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}
  </>
);
